import {
  createPresignedUploadUrl,
  getDownloadPresignedUrl,
  type UploadedDocumentDto,
} from '@/features/city-clerk/file'
import { toast } from 'react-toastify'
import i18n from '@/i18n/config'

export const sanitizeFileName = (fileName: string): string => {
  let sanitized = fileName.replace(/[\s.]+$/, '')

  if (!sanitized) {
    sanitized = 'untitled'
  }

  return sanitized
}

export const uploadToBlob = async (
  file: File,
  presignedUrl: string,
  abortControllerRef?: React.RefObject<AbortController | null>,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (abortControllerRef) {
      abortControllerRef.current = new AbortController()
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        toast.error(
          i18n.t('ui.toast.uploadFailedStatus', { status: xhr.status }),
        )
        reject(
          new Error(
            i18n.t('ui.toast.uploadFailedStatus', { status: xhr.status }),
          ),
        )
      }
    })

    xhr.addEventListener('error', () => {
      toast.error(i18n.t('ui.toast.uploadFailedNetwork'))
      reject(new Error(i18n.t('ui.toast.uploadFailedNetwork')))
    })

    xhr.addEventListener('abort', () => {
      toast.error(i18n.t('ui.toast.uploadCancelled'))
      reject(new Error(i18n.t('ui.toast.uploadCancelled')))
    })

    if (abortControllerRef?.current) {
      abortControllerRef.current.signal.addEventListener('abort', () => {
        xhr.abort()
      })
    }

    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')

    xhr.send(file)
  })
}

export const downloadFile = async (
  documentId: string,
  defaultFileName = 'file',
) => {
  const data = await getDownloadPresignedUrl(documentId)

  if (!data) {
    toast.error(i18n.t('ui.toast.fetchFileFailed'))
    return
  }

  const response = await fetch(data.presignedUrl)
  if (!response.ok) {
    toast.error(i18n.t('ui.toast.fetchFileFailed'))
    return
  }

  const blob = await response.blob()
  const blobUrl = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = blobUrl
  link.download = data.fileName || defaultFileName
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

export async function uploadAttachments(
  files: File[],
): Promise<UploadedDocumentDto[]> {
  const attachments: UploadedDocumentDto[] = []

  for (const file of files) {
    const sanitizedFileName = sanitizeFileName(file.name)
    const presigned = await createPresignedUploadUrl({
      FileName: sanitizedFileName,
    })

    await uploadToBlob(file, presigned.presignedUrl)

    attachments.push({
      name: sanitizedFileName,
      blobName: presigned.blobName,
      contentType: file.type,
      size: file.size,
    })
  }

  return attachments
}
